import clsx from "clsx";
import { ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "wouter";

import type { ThemeV1 } from "@/theme/schema";

import {
  enabledProducts,
  productIconAssets,
  productIconRegistry,
} from "./navigation";

export function ProductModeSwitcher({
  collapsed,
  onNavigate,
  theme,
}: {
  collapsed: boolean;
  onNavigate?: () => void;
  theme: ThemeV1;
}) {
  const [location] = useLocation();
  const [openAt, setOpenAt] = useState<string | null>(null);
  const open = openAt === location;
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const products = enabledProducts(theme);
  const current =
    products.find((product) => product.id === "racing") ?? products[0];

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent | KeyboardEvent) => {
      if (event instanceof KeyboardEvent) {
        if (event.key !== "Escape") return;
        setOpenAt(null);
        window.requestAnimationFrame(() => menuButtonRef.current?.focus());
        return;
      }
      if (rootRef.current?.contains(event.target as Node)) {
        return;
      }
      setOpenAt(null);
    };
    document.addEventListener("mousedown", close);
    window.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      window.removeEventListener("keydown", close);
    };
  }, [open]);

  if (!current) return null;

  const CurrentIcon = productIconRegistry[current.iconId];
  const currentAsset = productIconAssets[current.iconId];

  return (
    <div
      className={clsx(
        "product-switcher",
        collapsed && "is-collapsed",
        open && "is-open",
      )}
      ref={rootRef}
    >
      <Link
        aria-current="page"
        aria-label={current.label}
        className="product-switcher__current"
        href={current.href ?? "/app/"}
        onClick={onNavigate}
      >
        <ProductIcon
          Icon={CurrentIcon}
          asset={currentAsset}
          className="product-switcher__icon"
        />
        <span className="product-switcher__label">{current.shortLabel}</span>
      </Link>
      <button
        aria-controls="monkeytilt-product-menu"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Open product menu"
        className="product-switcher__menu-button"
        onClick={() => setOpenAt(open ? null : location)}
        ref={menuButtonRef}
        type="button"
      >
        <span className="product-switcher__collapsed-product">
          <ProductIcon Icon={CurrentIcon} asset={currentAsset} />
        </span>
        <ChevronDown aria-hidden />
      </button>

      <nav
        aria-label="MonkeyTilt products"
        className="product-switcher__dropdown"
        hidden={!open}
        id="monkeytilt-product-menu"
        role="menu"
      >
        {products.map((product) => {
          const Icon = productIconRegistry[product.iconId];
          const iconAsset = productIconAssets[product.iconId];
          const active = product.id === current.id;
          const content = (
            <>
              <ProductIcon
                Icon={Icon}
                asset={iconAsset}
                className="product-switcher__dropdown-icon"
              />
              <span>{product.label}</span>
              {active ? (
                <span className="product-switcher__sr-only">
                  Current product
                </span>
              ) : null}
            </>
          );
          const props = {
            "aria-current": active ? ("page" as const) : undefined,
            className: clsx(
              "product-switcher__dropdown-item",
              active && "is-current",
            ),
            onClick: () => {
              setOpenAt(null);
              onNavigate?.();
            },
            role: "menuitem",
          };
          return product.href?.startsWith("/") ? (
            <Link href={product.href} key={product.id} {...props}>
              {content}
            </Link>
          ) : (
            <a href={product.href ?? undefined} key={product.id} {...props}>
              {content}
            </a>
          );
        })}
      </nav>
    </div>
  );
}

function ProductIcon({
  Icon,
  asset,
  className,
}: {
  Icon: (typeof productIconRegistry)[keyof typeof productIconRegistry];
  asset: string | null;
  className?: string;
}) {
  return (
    <span className={className}>
      {asset ? <img alt="" aria-hidden src={asset} /> : <Icon aria-hidden />}
    </span>
  );
}
